// Loại tài liệu: thêm/sửa/xóa (chặn khi đang dùng), bật/tắt sử dụng, ký hiệu sinh mã, kéo thả sắp xếp
import { useMemo, useState } from 'react';
import { Button, Drawer, Form, Input, Modal, Select, Switch, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { GripVertical, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import DocShell from 'pages/Documents/components/DocShell';
import useDocsStore from 'pages/Documents/store';
import type { DocType } from 'pages/Documents/data';
import { matches, nextTypeCode } from 'pages/Documents/logic';
import { useDocAuth } from 'pages/Documents/utils';

type FormValues = Pick<DocType, 'code' | 'name' | 'prefix' | 'active'>;

export default function DocumentType() {
  const { can } = useDocAuth();
  const editable = can('config');
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const docs = useDocsStore(s => s.docs);
  const saveType = useDocsStore(s => s.saveType);
  const deleteType = useDocsStore(s => s.deleteType);
  const reorderTypes = useDocsStore(s => s.reorderTypes);
  const [modal, ctx] = Modal.useModal();
  const [form] = Form.useForm<FormValues>();

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [editing, setEditing] = useState<DocType | 'new' | null>(null);
  const [drag, setDrag] = useState<{ id: string; over?: string; pos?: 'top' | 'bottom' } | null>(null);

  const usage = useMemo(() => {
    const map: Record<string, { cats: number; docs: number }> = {};
    types.forEach(t => { map[t.id] = { cats: 0, docs: 0 }; });
    categories.forEach(c => { if (map[c.typeId]) map[c.typeId].cats++; });
    docs.forEach(d => { if (map[d.typeId]) map[d.typeId].docs++; });
    return map;
  }, [types, categories, docs]);

  const sorted = useMemo(() => [...types].sort((a, b) => a.order - b.order), [types]);
  const filtered = sorted.filter(t =>
    matches(q, t.code, t.name, t.prefix) && (status === 'all' || (status === 'active' ? t.active : !t.active)));
  const canDrag = editable && !q && status === 'all';

  const openForm = (t: DocType | 'new') => {
    setEditing(t);
    form.setFieldsValue(t === 'new'
      ? { code: nextTypeCode(types.map(x => x.code)), name: '', prefix: '', active: true }
      : { code: t.code, name: t.name, prefix: t.prefix, active: t.active });
  };

  const submit = async () => {
    const v = await form.validateFields().catch(() => null);
    if (!v) return;
    const values = { ...v, code: v.code.trim().toUpperCase(), prefix: v.prefix.trim().toUpperCase(), name: v.name.trim() };
    const prev = editing !== 'new' ? editing : null;
    const doSave = () => {
      saveType({ ...values, id: prev?.id });
      message.success(prev ? 'Đã cập nhật loại tài liệu' : 'Đã thêm loại tài liệu');
      setEditing(null);
    };
    if (prev && prev.prefix !== values.prefix && usage[prev.id]?.docs) {
      modal.confirm({
        title: 'Đổi ký hiệu loại tài liệu?',
        content: `Đã có ${usage[prev.id].docs} tài liệu dùng ký hiệu “${prev.prefix}”. Mã của các tài liệu cũ giữ nguyên, chỉ tài liệu mới dùng ký hiệu “${values.prefix}”.`,
        okText: 'Đồng ý', cancelText: 'Hủy', onOk: doSave,
      });
    } else doSave();
  };

  const toggleActive = (t: DocType) => {
    const u = usage[t.id];
    const apply = () => { saveType({ ...t, active: !t.active }); message.success(t.active ? 'Đã ngừng sử dụng' : 'Đã bật sử dụng'); };
    if (t.active && (u.cats || u.docs)) {
      modal.confirm({
        title: `Ngừng sử dụng “${t.name}”?`,
        content: `Loại này đang có ${u.cats} danh mục và ${u.docs} tài liệu. Sau khi ngừng, không chọn được loại này khi tạo danh mục / tài liệu mới; dữ liệu cũ giữ nguyên.`,
        okText: 'Ngừng sử dụng', cancelText: 'Hủy', onOk: apply,
      });
    } else apply();
  };

  const remove = (t: DocType) => {
    const u = usage[t.id];
    if (u.cats || u.docs) {
      modal.warning({
        title: 'Không thể xóa loại tài liệu đang được dùng',
        content: `“${t.name}” đang có ${u.cats} danh mục và ${u.docs} tài liệu (kể cả trong thùng rác). Hãy chuyển sang “Ngừng sử dụng” thay vì xóa.`,
        okText: 'Đã hiểu',
      });
      return;
    }
    modal.confirm({
      title: 'Xóa loại tài liệu?', content: `Xóa “${t.name}”. Hành động này không thể hoàn tác.`, okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy',
      onOk: () => { deleteType(t.id); message.success('Đã xóa loại tài liệu'); },
    });
  };

  const drop = (targetId: string) => {
    if (!drag || drag.id === targetId) return setDrag(null);
    const ids = sorted.map(t => t.id).filter(id => id !== drag.id);
    const idx = ids.indexOf(targetId) + (drag.pos === 'bottom' ? 1 : 0);
    ids.splice(idx, 0, drag.id);
    reorderTypes(ids);
    setDrag(null);
  };

  const columns: ColumnsType<DocType> = [
    ...(editable ? [{
      key: 'drag', width: 44, align: 'center' as const,
      render: () => (
        <Tooltip title={canDrag ? 'Kéo thả để sắp xếp' : 'Bỏ lọc để sắp xếp'}>
          <GripVertical size={16} className={canDrag ? 'cursor-grab text-slate-400' : 'text-slate-200'} />
        </Tooltip>
      ),
    }] : []),
    { title: 'STT', key: 'stt', width: 64, render: (_v, _r, i) => <span className="text-slate-500">{String(i + 1).padStart(2, '0')}</span> },
    { title: 'Mã loại', dataIndex: 'code', width: 120, render: v => <span className="font-medium text-[#357f96]">{v}</span> },
    { title: 'Tên loại tài liệu', dataIndex: 'name', render: v => <span className="font-medium text-slate-800">{v}</span> },
    {
      title: <Tooltip title="Dùng để sinh mã tài liệu, vd 001/2026/QĐ-KHOTHI">Ký hiệu</Tooltip>,
      dataIndex: 'prefix', width: 100, render: v => <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs">{v}</span>,
    },
    { title: 'Danh mục', key: 'cats', width: 100, align: 'right', render: (_v, t) => usage[t.id]?.cats || 0 },
    { title: 'Tài liệu', key: 'docs', width: 100, align: 'right', render: (_v, t) => usage[t.id]?.docs || 0 },
    {
      title: 'Trạng thái', key: 'active', width: 190,
      render: (_v, t) => (
        <div className="flex items-center gap-2">
          <Switch size="small" checked={t.active} disabled={!editable} onChange={() => toggleActive(t)} />
          <span className="text-sm text-slate-600">{t.active ? 'Đang sử dụng' : 'Ngừng sử dụng'}</span>
        </div>
      ),
    },
    ...(editable ? [{
      title: 'Hành động', key: 'act', width: 110, align: 'center' as const,
      render: (_v: unknown, t: DocType) => (
        <div className="flex items-center justify-center gap-3">
          <Tooltip title="Sửa"><button onClick={() => openForm(t)} className="text-slate-400 hover:text-[#357f96]"><Pencil size={16} /></button></Tooltip>
          <Tooltip title="Xóa"><button onClick={() => remove(t)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button></Tooltip>
        </div>
      ),
    }] : []),
  ];

  const others = editing === 'new' ? types : types.filter(t => editing && t.id !== editing.id);

  return (
    <DocShell
      title="Loại tài liệu"
      subtitle={`${types.length} loại · ${types.filter(t => t.active).length} đang sử dụng`}
      actions={editable && <Button type="primary" icon={<Plus size={16} />} onClick={() => openForm('new')}>Tạo mới</Button>}
    >
      {ctx}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input allowClear prefix={<Search size={15} className="text-slate-400" />} placeholder="Tìm mã, tên, ký hiệu…" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" />
        <Select value={status} onChange={setStatus} className="w-44" options={[
          { value: 'all', label: 'Tất cả trạng thái' }, { value: 'active', label: 'Đang sử dụng' }, { value: 'inactive', label: 'Ngừng sử dụng' },
        ]} />
      </div>
      <div className="rounded-lg border border-slate-200 bg-white">
        <Table<DocType>
          rowKey="id" columns={columns} dataSource={filtered} scroll={{ x: 900 }}
          locale={{ emptyText: q || status !== 'all' ? 'Không có loại tài liệu phù hợp bộ lọc' : 'Chưa có loại tài liệu' }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50], showTotal: (t, r) => `${r[0]}-${r[1]} trong ${t} loại`, hideOnSinglePage: false }}
          onRow={t => (canDrag ? {
            draggable: true,
            onDragStart: e => { e.dataTransfer.effectAllowed = 'move'; setDrag({ id: t.id }); },
            onDragOver: e => {
              e.preventDefault();
              if (!drag || drag.id === t.id) return;
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const pos = e.clientY - r.top > r.height / 2 ? 'bottom' : 'top';
              if (drag.over !== t.id || drag.pos !== pos) setDrag({ ...drag, over: t.id, pos });
            },
            onDrop: e => { e.preventDefault(); drop(t.id); },
            onDragEnd: () => setDrag(null),
            className: [
              drag?.id === t.id ? 'opacity-50' : '',
              drag?.over === t.id ? (drag.pos === 'top' ? '[&>td]:!border-t-2 [&>td]:!border-t-[#3e91ab]' : '[&>td]:!border-b-2 [&>td]:!border-b-[#3e91ab]') : '',
            ].join(' '),
          } : {})}
        />
      </div>

      <Drawer
        open={!!editing} onClose={() => setEditing(null)} width={420} destroyOnHidden
        title={editing === 'new' ? 'Thêm mới loại tài liệu' : 'Cập nhật loại tài liệu'}
        footer={<div className="flex justify-end gap-2"><Button onClick={() => setEditing(null)}>Hủy bỏ</Button><Button type="primary" onClick={submit}>Lưu</Button></div>}
      >
        <Form form={form} layout="vertical" onFinish={submit} requiredMark>
          <Form.Item
            name="code" label="Mã loại tài liệu"
            rules={[
              { required: true, whitespace: true, message: 'Nhập mã loại' },
              { pattern: /^[A-Za-z0-9._-]{2,20}$/, message: '2–20 ký tự: chữ không dấu, số, . _ -' },
              { validator: (_r, v) => (v && others.some(t => t.code.toUpperCase() === String(v).trim().toUpperCase()) ? Promise.reject(new Error('Mã đã tồn tại')) : Promise.resolve()) },
            ]}
            extra="Hệ thống gợi ý mã kế tiếp, có thể sửa."
          >
            <Input placeholder="VD: G0009" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="name" label="Tên loại tài liệu"
            rules={[
              { required: true, whitespace: true, message: 'Nhập tên loại' },
              { max: 100, message: 'Tối đa 100 ký tự' },
              { validator: (_r, v) => (v && others.some(t => t.name.trim().toLowerCase() === String(v).trim().toLowerCase()) ? Promise.reject(new Error('Tên đã tồn tại')) : Promise.resolve()) },
            ]}
          >
            <Input placeholder="VD: Hướng dẫn" maxLength={100} showCount />
          </Form.Item>
          <Form.Item
            name="prefix" label="Ký hiệu"
            rules={[
              { required: true, whitespace: true, message: 'Nhập ký hiệu' },
              { pattern: /^[A-Za-zĐđ0-9]{1,6}$/, message: '1–6 ký tự chữ/số, không khoảng trắng' },
              { validator: (_r, v) => (v && others.some(t => t.prefix.toUpperCase() === String(v).trim().toUpperCase()) ? Promise.reject(new Error('Ký hiệu đã được loại khác dùng')) : Promise.resolve()) },
            ]}
            extra="Dùng sinh mã tài liệu: 001/2026/<ký hiệu>-KHOTHI"
          >
            <Input placeholder="VD: HD" maxLength={6} className="uppercase" />
          </Form.Item>
          <Form.Item name="active" label="Trạng thái" valuePropName="checked">
            <Switch checkedChildren="Đang sử dụng" unCheckedChildren="Ngừng" />
          </Form.Item>
          <button type="submit" hidden />
        </Form>
      </Drawer>
    </DocShell>
  );
}
